import React, { useEffect, useMemo, useState } from 'react';
import type { NPC, Quest } from '../../../../shared/NPCData';
import { NPCInteractionUIManager } from '../../npc/NPCInteractionUIManager';
import { QuestView } from './QuestView';
import { NpcTitleBlock } from './NpcPanelChrome';
import { NPC_UI_VARS } from './npcPresentation';
import { TomeButton, TomePanel } from '../../primitives/tome';
import { npcTomeVibe } from './npcTomeVibe';
import { npcQuestEntryCount } from '../../npc/npcQuestEntries';
import { useInventory } from '../../hooks/useInventory';
import { useColyseusMessages } from '../../hooks/useColyseusMessages';
import { actionFailureMessage } from '../../hooks/useActionFailureFeedback';
import { turnInBlockFor } from './turnInBlock';
import type { QuestCompletionNotice } from '../../contexts/InteractionContext';
import { NpcCompletionNotice } from './NpcCompletionNotice';
import './NPCPlayerUI.css';

interface QuestOfferWindowProps {
  npc: NPC;
  playerLevel: number;
  onQuestAccept?: (quest: Quest) => void;
  onQuestComplete?: (quest: Quest) => void;
  onBrowseShop?: () => void;
  onClose: () => void;
  /** A quest just turned in here: its line leads the refreshed page (plan 13 §10). */
  completion?: QuestCompletionNotice | null;
}

/** Quest eligibility and request callbacks keep their current interaction owner. */
export const QuestOfferWindow: React.FC<QuestOfferWindowProps> = ({
  npc, playerLevel, onQuestAccept, onQuestComplete, onBrowseShop, onClose, completion,
}) => {
  const manager = useMemo(() => new NPCInteractionUIManager({
    npc, playerLevel, callbacks: { onQuestAccept, onQuestComplete },
  }), [npc, playerLevel, onQuestAccept, onQuestComplete]);
  const isQuestAvailable = (quest: Quest) => manager.quests
    ? manager.quests.isQuestAvailable(quest) : playerLevel >= quest.requiredLevel;
  const isQuestCompletable = (quest: Quest) => manager.quests
    ? manager.quests.isQuestCompletable(quest) : quest.isAccepted && quest.isTurnInReady && !quest.isCompleted;
  // Plan 06 §2.4: explain a blocked turn-in before the click, and show the
  // server's refusal here as well as in chat.
  const { items, maxSlots } = useInventory();
  const [questError, setQuestError] = useState<string | null>(null);
  useColyseusMessages({ questError: (payload: unknown) => setQuestError(actionFailureMessage('questError', payload)) });
  // A refusal stays until the quests it was about change (the overlay rebuilds `npc` every render).
  const questSignature = npc.quests.map(q => `${q.id}:${q.isAccepted}:${q.isTurnInReady}:${q.isCompleted}`).join('|');
  useEffect(() => { setQuestError(null); }, [npc.id, questSignature]);

  const hasShop = Boolean(npc.shopItems && npc.shopItems.length > 0 && onBrowseShop);
  const questCount = npcQuestEntryCount(npc);
  const toolbar = hasShop ? (
    <nav className="npc-panel-actions" aria-label="NPC navigation">
      <TomeButton tone="primary" aria-pressed="true" disabled>
        Quests ({questCount})
      </TomeButton>
      <TomeButton tone="secondary" onClick={onBrowseShop} data-controller-return="shop">
        Browse Shop ({npc.shopItems.length})
      </TomeButton>
    </nav>
  ) : undefined;

  return <TomePanel className="npc-player-panel npc-quest-panel" role="dialog" shapeSeed={`${npc.id}:quests`} vibe={npcTomeVibe(npc.id)}
    aria-label={`${npc.name}'s quests`} data-controller-surface="npc-quests" style={{ ...NPC_UI_VARS, zIndex: 220 }}
    header={<NpcTitleBlock npcId={npc.id} npcType={npc.type} name={npc.name} subtitle="Quests & requests"
      onClose={onClose} closeLabel="Close quests" />}
    toolbar={toolbar}
    notice={questError && <p className="npc-quest-error" role="alert" data-testid="quest-dialog-error">{questError}</p>}>
    {completion && <NpcCompletionNotice completion={completion} />}
    <QuestView npc={npc} playerLevel={playerLevel} showEmpty={!completion}
      npcTypeInfo={manager.getNPCTypeInfo()} isQuestAvailable={isQuestAvailable} isQuestCompletable={isQuestCompletable}
      onQuestAccept={onQuestAccept} onQuestComplete={onQuestComplete}
      turnInBlockFor={quest => turnInBlockFor(quest, items, maxSlots)} />
    {hasShop && (
      <div className="npc-panel-actions" style={{ marginTop: '14px', justifyContent: 'center' }}>
        <TomeButton tone="secondary" fullWidth onClick={onBrowseShop} data-controller-return="shop">
          Browse Shop ({npc.shopItems.length} items)
        </TomeButton>
      </div>
    )}
  </TomePanel>;
};
